import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { SignatureRequestEvent, Workflow } from "../interfaces";
import { getData } from "../backend";
import { SIGNATURES_ACTIVITY_MAP, SIGNATURES_STATUS_MAP } from "../utils";
import { useHistory } from "react-router-dom";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import { ClipboardCopyIcon, ExternalLinkIcon, RefreshIcon } from "@heroicons/react/outline";
import CopyToClipboard from "react-copy-to-clipboard";

interface Props {
  workflow: Workflow;
  handleBack: () => void;
  handleRefresh: () => void;
}

interface ActivityWithRecipients extends SignatureRequestEvent {
  recipientNumber: number;
  recipientEmail: string;
}

function SignaturesSidebarOverview(props: Props) {
  const { t } = useTranslation();
  const history = useHistory();

  // NOTE: we get the SignatureWorkflowSummary here as it contains signature specific derived fields
  const { data, mutate, isValidating } = useSWR<{ "results": Array<Workflow> }>("/signatures/workflows/?id=" + props.workflow.id, getData);
  const workflow = data?.results[0] ?? props.workflow;

  const [copiedSignatureRequest, setCopiedSignatureRequest] = useState<number>();

  function handleDownload() {
    getData(`/signatures/document-url/${workflow.id}/`)
      .then(({ url }) => window.open(url, "_blank"))
      .catch(console.error)
  }

  const allEvents = useMemo(() => {
    if (!workflow.primaryItem?.signatureRequests)
      return []

    let events: Array<ActivityWithRecipients> = [];
    for (const sr of workflow.primaryItem?.signatureRequests)
      events = [...events, ...(sr.events.map(event => ({ ...event, recipientEmail: sr.recipientEmail, recipientNumber: sr.recipientNumber })))]

    return events;
  }, [workflow.primaryItem?.signatureRequests]);

  function handleOnCopiedSignatureRequest(recipient: number) {
    setCopiedSignatureRequest(recipient);
    setTimeout(() => setCopiedSignatureRequest(undefined), 2000);
  }

  return (

    <div className="flex flex-col w-[360px] h-full bg-white flex-shrink-0 pt-6 px-4 border-l">

      <div className="flex items-center mb-2 gap-2">
        <div className="bg-lightblue rounded p-2">
          <img className="w-10 h-10" src="https://static.portant.co/portant-sign-icon.png" alt="Portant Sign Icon" />
        </div>
        <div className="flex flex-col">
          <div className="text-2xl font-semibold font-gilroy">
            Signature Request
          </div>
          <div className="text-xs font-semibold text-gray-400">
            {t("Powered by Portant Sign")}
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0" />

      <div className="flex items-center justify-between mt-4 mb-2">
        <div className="font-semibold font-gilroy">
          Overview
        </div>
        <button onClick={() => { mutate(); props.handleRefresh() }}>
          <RefreshIcon className={`text-gray-400 hover:text-gray-600 h-5 w-5 ${(isValidating) ? "animate-spin" : "animate-none"}`} />
        </button>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <div className="text-sm text-gray-400">
            Name
          </div>
          <div className="font-semibold text-gray-600 text-sm truncate">
            {workflow.name}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex flex-col w-1/2">
            <div className="text-sm text-gray-400">
              Status
            </div>
            <div className="font-semibold text-gray-600 text-sm truncate">
              <div className={`w-fit px-4 font-semibold rounded-full cursor-pointer border ${SIGNATURES_STATUS_MAP[workflow.derivedStatus ?? ""]?.[1]}`} onClick={() => history.push("/s/" + workflow.id)}>
                {SIGNATURES_STATUS_MAP[workflow.derivedStatus ?? ""]?.[0]}
              </div>
            </div>
          </div>

          <div className="flex flex-col w-1/2">
            <div className="text-sm text-gray-400">
              Sender
            </div>
            <div className="font-semibold text-gray-600 text-sm truncate">
              {workflow.owner?.fullName}
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="text-sm text-gray-400">
            Created
          </div>
          <div className="font-semibold text-gray-600 text-sm truncate">
            {moment(workflow.createdAt).toLocaleString()}
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-4" />

      <div className="font-semibold font-gilroy mt-4 mb-2">
        Recipients
      </div>

      <div className="flex flex-col gap-2">
        {workflow.primaryItem?.signatureRequests?.map((sr) =>
          <div key={sr.id}>
            <div className="flex items-center w-full text-sm text-gray-600">
              <div className="flex-shrink-0 w-5 text-gray-400 font-semibold">{sr.recipientNumber}.</div>
              <div className="w-full font-semibold truncate">{sr.recipientEmail}</div>
              <div className="flex-shrink-0">
                <div className={`w-fit px-4 font-semibold rounded-full border ${SIGNATURES_STATUS_MAP[sr.status ?? ""]?.[1]}`}>
                  {SIGNATURES_STATUS_MAP[sr.status ?? ""]?.[0]}
                </div>
              </div>
            </div>
            {(sr.recipientNumber > 0 && (sr.status === "REQUESTED" || sr.status === "ACCESSED")) &&
              <div className="flex items-center mt-2">
                <div className="text-xs text-gray-600">{t("Signature Request link")}:</div>
                <a className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1" target={sr.publicTokenUrl} href={sr.publicTokenUrl} rel="noreferrer">
                  <ExternalLinkIcon className="w-4 h-4 flex-shrink-0" /> {t("Open")}
                </a>
                <CopyToClipboard text={sr.publicTokenUrl} onCopy={() => handleOnCopiedSignatureRequest(sr.recipientNumber)}>
                  {copiedSignatureRequest === sr.recipientNumber
                    ? <div className="flex items-center gap-1 text-xs text-blue ml-2 font-semibold select-none -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copied")}</div>
                    : <div className="flex items-center gap-1 text-xs text-gray-600 ml-2 hover:underline cursor-pointer -mt-1"><ClipboardCopyIcon className="w-4 h-4 flex-shrink-0" /> {t("Copy")}</div>
                  }
                </CopyToClipboard>
              </div>
            }
          </div>
        )}
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-4" />

      <div className="font-semibold font-gilroy mt-4 mb-2">
        Activity
      </div>

      <div className="flex flex-col gap-2 mb-2 overflow-y-scroll">
        {allEvents.map(event => (
          <div key={event.eventType + "-" + event.createdAt} className="flex flex-col gap-1">
            <div className="text-xs text-gray-400">
              {moment(event.createdAt).toLocaleString()}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="font-semibold text-gray-600 truncate">
                {event.recipientEmail} ({event.recipientNumber})
              </div>
              {event.eventType === "REQUESTED" &&
                <div className="w-fit px-4 font-semibold rounded-full text-blue bg-blue/10 flex-shrink-0">
                  {t("Sent")}
                </div>
              }
              {event.eventType === "COMPLETED" &&
                <div className="w-fit px-4 font-semibold rounded-full text-green bg-green/10 flex-shrink-0">
                  {t("Signed")}
                </div>
              }
              {!["REQUESTED", "COMPLETED"].includes(event.eventType) &&
                <div className="w-fit px-4 font-semibold rounded-full text-gray-400 bg-gray-100 flex-shrink-0">
                  {SIGNATURES_ACTIVITY_MAP[event.eventType ?? ""]}
                </div>
              }
            </div>
          </div>
        ))}
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
      <div className="flex items-center text-sm gap-2 mt-4 mb-4">
        <button className="btn btn-gray border w-full px-0 py-2" onClick={props.handleBack}>Back</button>
        <button className="btn btn-blue w-full px-0 py-2 flex items-center" onClick={handleDownload}>Download</button>
      </div>
    </div>
  );

}

export default SignaturesSidebarOverview;
